import { camelToSnake, snakeToCamel } from "caseparser";

import { RequestMethod } from "@/shared/utils/RequestMethod";
import { Fee, FeeHours } from "./fees";
import { api } from "@/app/app.api";

export const feesApi = api
  .enhanceEndpoints({ addTagTypes: ["fees", "influencer"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      fees: builder.mutation({
        query: ({ branchId, search, page }: any) => {
          const params = new URLSearchParams();
          params.set("search", search ? search : "");
          params.set("page", page ? page : "");

          return {
            url: `branchs/${branchId}/fees?${params.toString()}`,
            method: RequestMethod.GET,
          };
        },
        transformResponse: (response: any): Array<Fee> =>
          snakeToCamel(response?.data).map((fee: Fee) => ({
            ...fee,
            hourInit: fee.feeSchedules.reduce<FeeHours>(
              (hours: FeeHours, feeSchedule) => ({
                ...hours,
                [feeSchedule.day]: feeSchedule.startTime,
              }),
              {}
            ),
            hourFinish: fee.feeSchedules.reduce<FeeHours>(
              (hours: FeeHours, feeSchedule) => ({
                ...hours,
                [feeSchedule.day]: feeSchedule.endTime,
              }),
              {}
            ),
          })),
        invalidatesTags: ["fees"],
      }),
      createFee: builder.mutation({
        query: ({
          branchId,
          name,
          description,
          currency,
          price,
          interval,
          trialPeriodDays,
          automatedTraining,
          automatedNutrition,
          people,
          days,
          hourInit,
          hourFinish,
          installations,
          centers,
          centerAccess,
          discount,
          registrationPaymentId,
          app,
        }) => ({
          url: `branchs/${branchId}/fees`,
          method: RequestMethod.POST,
          body: {
            name,
            description,
            currency,
            price: price,
            interval,
            trial_period_days: trialPeriodDays,
            automated_training: automatedTraining,
            automated_nutrition: automatedNutrition,
            people,
            days,
            hour_init: hourInit,
            hour_finish: hourFinish,
            installations,
            centers,
            center_access: centerAccess,
            discount,
            registration_payment: registrationPaymentId,
            app,
          },
        }),
        invalidatesTags: ["fees"],
      }),
      updateFee: builder.mutation({
        query: ({
          id,
          branchId,
          name,
          description,
          currency,
          amount,
          interval,
          trialPeriodDays,
          automatedTraining,
          automatedNutrition,
          people,
          days,
          hourInit,
          hourFinish,
          installations,
          centers,
          centerAccess,
          discount,
          registrationPaymentId,
          app,
        }) => ({
          url: `branchs/${branchId}/fees/${id}`,
          method: RequestMethod.POST,
          body: {
            name,
            description,
            currency,
            amount,
            interval,
            trial_period_days: trialPeriodDays,
            automated_training: automatedTraining,
            automated_nutrition: automatedNutrition,
            people,
            days,
            hour_init: hourInit,
            hour_finish: hourFinish,
            installations,
            centers,
            center_access: centerAccess,
            discount,
            registration_payment: registrationPaymentId,
            app,
          },
        }),
        invalidatesTags: ["fees"],
      }),
      kpiFees: builder.query({
        query: ({ branchId, from_date, to_date }) => {
          return {
            url: `branchs/${branchId}/fees/customer-to-gym-subscription/get-total-reason-type`,
            method: RequestMethod.POST,
            body: {
              branch_id: branchId,
              from_date,
              to_date,
            },
          };
        },
      }),
      disabledFee: builder.mutation({
        query: ({ branchId, fee_id, dateInitial, dateEnd, days }) => ({
          url: `/branchs/${branchId}/course-fee/fee/disable`,
          method: RequestMethod.POST,
          body: {
            fee_id,
            dateInitial,
            dateEnd,
            days,
          },
        }),
        invalidatesTags: ["fees"],
      }),
      disableFeeBranch: builder.mutation({
        query: ({ branchId, fee_id, dateInitial, dateEnd, days }) => ({
          url: `branchs/${branchId}/fees/disabled`,
          method: RequestMethod.POST,
          body: {
            fee_id,
            dateInitial,
            dateEnd,
            days,
          },
        }),
        invalidatesTags: ["fees"],
      }),
      influencer: builder.query({
        query: ({ branchId }: any) => ({
          url: `branchs/${branchId}/influencer`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["influencer"],
      }),
      priceNext: builder.mutation({
        query: ({ branchId, user_id, fee_id, price }) => ({
          url: `branchs/${branchId}/fees/price-next`,
          method: RequestMethod.POST,
          body: camelToSnake({
            user_id,
            fee_id,
            price,
          }),
        }),
        invalidatesTags: ["fees"],
      }),
      unsubscribeUser: builder.mutation({
        query: ({
          branchId,
          user_id,
          fee_id,
          reason_id,
          predicted_unsubscribe_date,
        }) => ({
          url: `branchs/${branchId}/fees/unsubscribe-fee-to-user`,
          method: RequestMethod.POST,
          body: camelToSnake({
            user_id,
            fee_id,
            reason_id,
            predicted_unsubscribe_date,
          }),
        }),
        invalidatesTags: ["fees"],
      }),
      deleteFeeToUser: builder.mutation({
        query: ({ branchId, user_id, fee_id }) => ({
          url: `branchs/${branchId}/fees/delete-fee-to-user`,
          method: RequestMethod.POST,
          body: camelToSnake({
            user_id,
            fee_id,
          }),
        }),
        invalidatesTags: ["fees"],
      }),
      pauseFeeToUser: builder.mutation({
        query: ({ branchId, user_id, fee_id, dateInitial, dateEnd, days }) => ({
          url: `branchs/${branchId}/fees/pause-fee-to-user`,
          method: RequestMethod.POST,
          body: {
            user_id,
            fee_id,
            dateInitial,
            dateEnd,
            days,
          },
        }),
        invalidatesTags: ["fees"],
      }),
      getStatusFeeUserDiscount: builder.query({
        query: ({ branchId, feeId, userId }) => ({
          url: `branchs/${branchId}/fees/status-fee-user-discount/${feeId}`,
          method: RequestMethod.POST,
          headers: { "Content-Type": "application/json" },
          body: camelToSnake({
            userId,
          }),
        }),
        transformResponse: (response: any) => response?.data,
      }),
    }),
  });

export const {
  useFeesMutation,
  useCreateFeeMutation,
  useUpdateFeeMutation,
  useKpiFeesQuery,
  useDisabledFeeMutation,
  useInfluencerQuery,
  usePriceNextMutation,
  useUnsubscribeUserMutation,
  useDeleteFeeToUserMutation,
  usePauseFeeToUserMutation,
  useGetStatusFeeUserDiscountQuery,
  useDisableFeeBranchMutation,
} = feesApi;
